import * as THREE from "three";

import { SceneManager } from "../../../core/scene_manager.js";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";

export class EscalatorLamp
{
  private static readonly BLINK_PERIOD_SEC = 0.7;
  private static readonly FLASH_HOLD = 0.12;
  private static readonly FLASH_DECAY = 7;
  private static readonly ON_INTENSITY = 1.6;
  private static readonly SIGNAL_COLOR = 0xff1414;
  private static readonly GLASS_CENTER_FRACTION = 0.94;
  private static readonly HALO_SIZE_FRACTION = 0.5;
  private static readonly HALO_MIN_SCALE = 0.75;
  private static readonly HALO_TEXTURE_SIZE = 128;

  private readonly root = new THREE.Group();
  private readonly material: THREE.MeshLambertMaterial;
  private readonly halo_texture: THREE.CanvasTexture;
  private readonly halo_material: THREE.SpriteMaterial;
  private readonly halo: THREE.Sprite;
  private readonly halo_size: number;
  private blinking = false;
  private time = 0;

  constructor( x: number, front_z: number, top_y: number, base_y: number )
  {
    this.material = new THREE.MeshLambertMaterial( {
      map: ResourceManager.getTexture( ResourceID.EscalatorLedBaseTexture ),
      emissive: new THREE.Color( EscalatorLamp.SIGNAL_COLOR ),
      emissiveMap: ResourceManager.getTexture( ResourceID.EscalatorLedEmissiveTexture ),
      emissiveIntensity: 0,
      clippingPlanes: [ new THREE.Plane( new THREE.Vector3( 0, 1, 0 ), -base_y ) ],
      clipShadows: true,
    } );

    const model = ResourceManager.getModel( ResourceID.LedModel );

    model.traverse( obj =>
    {
      if ( !( obj instanceof THREE.Mesh ) )
        return;

      obj.material = this.material;
      obj.castShadow = true;
      obj.receiveShadow = true;
    } );

    const size = new THREE.Box3().setFromObject( model ).getSize( new THREE.Vector3() );
    model.scale.multiplyScalar( top_y / size.y );
    model.updateMatrixWorld( true );

    const box = new THREE.Box3().setFromObject( model );
    const center = box.getCenter( new THREE.Vector3() );
    const half_depth = box.max.z - center.z;
    model.position.set( -center.x, -box.min.y, -center.z );

    this.halo_texture = EscalatorLamp.createHaloTexture();
    this.halo_material = new THREE.SpriteMaterial( {
      map: this.halo_texture,
      color: EscalatorLamp.SIGNAL_COLOR,
      blending: THREE.NormalBlending,
      transparent: true,
      depthWrite: false,
      opacity: 0,
    } );

    this.halo_size = top_y * EscalatorLamp.HALO_SIZE_FRACTION;
    this.halo = new THREE.Sprite( this.halo_material );
    this.halo.position.set( 0, top_y * EscalatorLamp.GLASS_CENTER_FRACTION, 0 );
    this.halo.scale.setScalar( this.halo_size );
    this.halo.visible = false;

    this.root.add( model );
    this.root.add( this.halo );
    this.root.position.set( x, 0, front_z - half_depth );
    SceneManager.world.add( this.root );
  }

  private static createHaloTexture(): THREE.CanvasTexture
  {
    const size = EscalatorLamp.HALO_TEXTURE_SIZE;
    const half = size * 0.5;
    const canvas = document.createElement( "canvas" );
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext( "2d" )!;
    const gradient = ctx.createRadialGradient( half, half, 0, half, half, half );
    gradient.addColorStop( 0, "rgba(255,255,255,0.6)" );
    gradient.addColorStop( 0.2, "rgba(255,255,255,0.5)" );
    gradient.addColorStop( 0.5, "rgba(255,255,255,0.22)" );
    gradient.addColorStop( 1, "rgba(255,255,255,0)" );
    ctx.fillStyle = gradient;
    ctx.fillRect( 0, 0, size, size );

    const texture = new THREE.CanvasTexture( canvas );
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  public startBlinking(): void
  {
    this.blinking = true;
    this.time = 0;
    this.halo.visible = true;
  }

  public stopBlinking(): void
  {
    this.blinking = false;
    this.material.emissiveIntensity = 0;
    this.halo.visible = false;
  }

  public update( dt: number ): void
  {
    if ( !this.blinking )
      return;

    this.time += dt;

    const t = ( this.time % EscalatorLamp.BLINK_PERIOD_SEC ) / EscalatorLamp.BLINK_PERIOD_SEC;
    const fade = Math.max( 0, t - EscalatorLamp.FLASH_HOLD );
    const pulse = Math.exp( -fade * EscalatorLamp.FLASH_DECAY );
    const halo_scale = EscalatorLamp.HALO_MIN_SCALE + ( 1 - EscalatorLamp.HALO_MIN_SCALE ) * pulse;

    this.material.emissiveIntensity = EscalatorLamp.ON_INTENSITY * pulse;
    this.halo_material.opacity = pulse;
    this.halo.scale.setScalar( this.halo_size * halo_scale );
  }

  public dispose(): void
  {
    SceneManager.world.remove( this.root );
    this.material.dispose();
    this.halo_material.dispose();
    this.halo_texture.dispose();
  }
}
